import { concat, path, pipe } from 'ramda';
import { InfoTooltip, Input, Select } from '@fasstech/middle-design-system';
import { isNilOrEmpty } from '../../../../../../server/helpers/index.mjs';
import useControlledSocialSecurityOrganismCode from './useControlledSocialSecurityOrganismCode';

const CustomError = ({ children }) => <div className="text-red-500">{children}</div>;

const Label = ({ children, required, tooltipText, className = '' }) => (
  <div className="relative w-fit mb-6">
    <label className={className}>
      {children} {required && '*'} {tooltipText && <InfoTooltip text={tooltipText} />}
    </label>
  </div>
);

const FieldContainer = ({ children }) => (
  <div className="w-full flex flex-col justify-between">{children}</div>
);
const selectClassNames = (disabled) => `bg-white rounded-sm ${disabled ? 'bg-gray-100!' : ''}`;

const ControlledSocialSecurityOrganismCode = ({
  rules = {},
  name,
  regimeName,
  regimeObligatoire = {},
  getMiddleText,
  className = '',
  tooltipOrganismCode,
  labelOrganismCode,
  tooltipRegime,
  tooltipCaisse,
  tooltipCentre,
  isDisplayed = true,
  ...extraProps
}) => {
  if (!isDisplayed) {
    return null;
  }
  const {
    formikProps,
    onCenterCodeChange,
    hasCenterCodeError,
    selectedRegimeCodeOption,
    selectedFundCodeOption,
    regimeCodeOptions,
    fundCodeOptions,
    regime,
    regimeCode,
    fundCode,
    centerCode
  } = useControlledSocialSecurityOrganismCode({
    rules,
    name,
    regimeName,
    regimeObligatoire
  });

  return (
    <div className={`border rounded-md bg-gray-100 px-8 py-4 w-full ${className}`}>
      <Label className="font-semibold flex items-center gap-2" tooltipText={tooltipOrganismCode}>
        {labelOrganismCode}
      </Label>

      <div className="grid grid-cols-1 md:grid-cols-3! gap-4">
        <FieldContainer>
          <Select
            label={getMiddleText('ORGANISM_CODE_REGIME_INPUT_LABEL')}
            {...formikProps}
            {...formikProps.field}
            {...extraProps.RegimeCodeSelectProps}
            options={regimeCodeOptions}
            onChange={pipe(path(['target', 'value']), formikProps.field.onChange)}
            value={selectedRegimeCodeOption?.value}
            defaultValue={''}
            disabled={isNilOrEmpty(regime)}
            className={selectClassNames(isNilOrEmpty(regime))}
            data-testid="ORGANISM_CODE_REGIME"
            tooltip={tooltipRegime}
            displayLabel={true}
          />
        </FieldContainer>

        <FieldContainer>
          <Select
            {...formikProps}
            {...formikProps.field}
            {...extraProps.FundCodeSelectProps}
            label={getMiddleText('ORGANISM_CODE_FUND_INPUT_LABEL')}
            options={fundCodeOptions}
            onChange={pipe(
              path(['target', 'value']),
              concat(regimeCode),
              formikProps.field.onChange
            )}
            value={selectedFundCodeOption?.value}
            defaultValue={''}
            disabled={isNilOrEmpty(regimeCode)}
            className={selectClassNames(isNilOrEmpty(regimeCode))}
            data-testid="ORGANISM_CODE_FUND"
            tooltip={tooltipCaisse}
            displayLabel={true}
          />
        </FieldContainer>

        <FieldContainer>
          <Input
            {...formikProps}
            {...formikProps.field}
            {...extraProps.CenterCodeSelectProps}
            label={getMiddleText('ORGANISM_CODE_CENTER_INPUT_LABEL')}
            value={centerCode}
            onChange={onCenterCodeChange}
            className={
              hasCenterCodeError
                ? `${selectClassNames(isNilOrEmpty(fundCode))} border-red-500`
                : selectClassNames(isNilOrEmpty(fundCode))
            }
            disabled={isNilOrEmpty(fundCode)}
            placeholder={getMiddleText('ORGANISM_CODE_CENTER_INPUT_PLACEHOLDER')}
            withIndicator
            error={hasCenterCodeError || centerCode === null || selectedFundCodeOption === null}
            data-testid="ORGANISM_CODE_CENTER"
            tooltip={tooltipCentre}
            displayLabel={true}
          />
          {hasCenterCodeError && (
            <CustomError>{getMiddleText('ORGANISM_CODE_CENTER_LENGTH_ERROR')}</CustomError>
          )}
        </FieldContainer>
      </div>
    </div>
  );
};

export default ControlledSocialSecurityOrganismCode;
